/**
 * useCmdKLauncher — minimal launcher hook for the Cmd-K palette.
 *
 * Owns ONE thing: the Cmd-K hotkey and the open/close state. Returns
 * `{ isOpen, open, close }`.
 *
 * Why it exists (LCP wave 3 finale):
 *   - The full `useCmdK` hook (sibling file) imports Fuse.js + the
 *     pages/mentees/glossary indices. That's ~30 kB of vendor + index
 *     code that previously rode on the entry bundle because Layout
 *     mounted `<CmdKPalette>` eagerly.
 *   - This launcher is purposefully thin — no Fuse, no indices, no
 *     persistence, no React reconciler churn on every key — so Layout
 *     can hold it on the entry bundle while lazy-importing the actual
 *     palette UI on the FIRST press.
 *
 * Wiring:
 *   - Layout calls `const launcher = useCmdKLauncher()` eagerly.
 *   - When `launcher.isOpen`, Layout renders the lazy `<CmdKPalette>`
 *     and passes the launcher's open/close pair through the palette's
 *     `external` prop so the palette stays in lockstep without
 *     re-registering its own Cmd-K hotkey (last-push-wins on the
 *     SSOT scope stack would clash otherwise).
 *
 * Discipline:
 *   - useKeyboardScope SSOT — registers ONE shortcut, never a bare
 *     window listener (WS-GOV-1 rule 8).
 *   - No side-effects, no captureError calls in the handler body
 *     (pure state mutation).
 *   - SRP: this hook only owns the open/close + hotkey contract.
 *     Anything richer (results, recents, fuzzy search) lives in
 *     `useCmdK`, behind the lazy import boundary.
 */

import { useCallback, useMemo, useState } from 'react';
import { useKeyboardScope } from '../keyboard';

export type UseCmdKLauncherResult = {
    /** True when the palette should be mounted. */
    isOpen: boolean;
    /** Force open (e.g. from a programmatic trigger). */
    open: () => void;
    /** Close the palette. Idempotent. */
    close: () => void;
};

export function useCmdKLauncher(): UseCmdKLauncherResult {
    const [isOpen, setIsOpen] = useState(false);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);

    // Cmd-K / Ctrl-K toggle — routed through the keyboard SSOT so it
    // composes with palette + modal scopes through the stack
    // discipline (last push wins). `preventDefault` so the browser's
    // own bookmark / focus-bar shortcut doesn't fire underneath.
    const shortcuts = useMemo(
        () => [
            {
                combo: 'mod+k',
                handler: (e: KeyboardEvent) => {
                    e.preventDefault();
                    setIsOpen((prev) => !prev);
                },
            },
        ],
        [],
    );
    useKeyboardScope(shortcuts, { owner: 'useCmdKLauncher' });

    return { isOpen, open, close };
}
